/** Pure helpers for the Schedule and Settings screens. Days are 0 = Monday … 6 = Sunday; times are Eastern. */
import type { ScheduleDay, ScheduleSlot, Slots } from './api'

export const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const
export const DAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const
const TZ = 'America/New_York'

/** "7:30 AM" from "07:30" or "07:30:00". */
export function clock12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

/** "Monday, Wednesday and Friday at 7:30 AM ET"; null with no days. */
export function slotsText(days: number[], hhmm: string): string | null {
  const names = [...new Set(days)].sort((a, b) => a - b).filter((d) => d >= 0 && d <= 6).map((d) => DAY_NAMES[d])
  if (names.length === 0) return null
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
  return `${list} at ${clock12(hhmm)} ET`
}

/** A legacy UTC cadence read as Eastern, through today's offset; Eastern slots come back unchanged. */
export function legacyToEt(slots: Slots, now: Date = new Date()): { days_of_week: number[]; release_time: string; converted: boolean } {
  if (slots.tz !== 'UTC') return { days_of_week: slots.days_of_week, release_time: slots.release_time.slice(0, 5), converted: false }
  const [h, m] = slots.release_time.split(':').map(Number)
  const utc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), h, m))
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: TZ, hourCycle: 'h23', hour: '2-digit', minute: '2-digit', year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(utc)
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value)
  const etDay = Date.UTC(get('year'), get('month') - 1, get('day'))
  const utcDay = Date.UTC(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate())
  const shift = Math.round((etDay - utcDay) / 86400000)
  const days = [...new Set(slots.days_of_week.map((d) => (((d + shift) % 7) + 7) % 7))].sort((a, b) => a - b)
  return { days_of_week: days, release_time: `${String(get('hour')).padStart(2, '0')}:${String(get('minute')).padStart(2, '0')}`, converted: true }
}

export type StripDay = {
  date: string
  /** "Mon 6" */
  label: string
  /** "Mon 6 Oct" */
  long: string
  slots: ScheduleSlot[]
  /** full: every slot has a piece; open: a slot has none; none: no slot that day. */
  state: 'full' | 'open' | 'none'
}

function parts(date: string): { dow: number; day: number; month: string } {
  const d = new Date(`${date}T12:00:00Z`)
  return {
    dow: (d.getUTCDay() + 6) % 7,
    day: d.getUTCDate(),
    month: d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short' }),
  }
}

/** The server's 14 Eastern days as strip cells. */
export function etDayStrip(days: ScheduleDay[]): StripDay[] {
  return days.map((d) => {
    const p = parts(d.date)
    return {
      date: d.date,
      label: `${DAY_SHORT[p.dow]} ${p.day}`,
      long: `${DAY_SHORT[p.dow]} ${p.day} ${p.month}`,
      slots: d.slots,
      state: d.slots.length === 0 ? 'none' : d.slots.some((s) => !s.piece) ? 'open' : 'full',
    }
  })
}

/** "Mon 13 Oct is open." for the first day with an empty slot; null when every slot is filled. */
export function openSlotLabel(strip: StripDay[]): string | null {
  const d = strip.find((x) => x.state === 'open')
  return d ? `${d.long} is open.` : null
}

/** "Mon" / "Tue" … for the toggle buttons. */
export const toggleDay = (days: number[], d: number): number[] =>
  (days.includes(d) ? days.filter((x) => x !== d) : [...days, d]).sort((a, b) => a - b)
